import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Spinner,
} from '../../../shared/components/ui';
import { jobsApi } from '../../../shared/services/jobsApi';
import {
  formatCurrency,
  formatSqFt,
  formatDate,
} from '../../../shared/utils/formatters';

function BackButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors mb-6"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 19l-7-7 7-7"
        />
      </svg>
      Back to Jobs
    </button>
  );
}

function JobHeader({ job }) {
  const getOutcomeBadge = (outcome) => {
    switch (outcome) {
      case 'profitable':
        return <Badge variant="green" size="lg">Profitable</Badge>;
      case 'break-even':
        return <Badge variant="yellow" size="lg">Break-even</Badge>;
      case 'loss':
        return <Badge variant="red" size="lg">Loss</Badge>;
      default:
        return <Badge variant="gray" size="lg">Unknown</Badge>;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 mb-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{job.projectName}</h1>
            {getOutcomeBadge(job.outcome)}
          </div>
          <p className="text-gray-600 dark:text-gray-300">{job.customer}</p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {formatCurrency(job.finalCost)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Final Cost</p>
        </div>
      </div>
    </div>
  );
}

function JobOverview({ job }) {
  const details = [
    { label: 'Job Type', value: job.jobType },
    { label: 'Location', value: job.location },
    { label: 'Square Footage', value: formatSqFt(job.squareFootage) },
    { label: 'Cost per Sq Ft', value: formatCurrency(job.finalCost / job.squareFootage) },
    { label: 'Completed', value: formatDate(job.completedDate) },
    { label: 'Margin', value: job.margin ? `${job.margin}%` : '—' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Project Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {details.map((detail) => (
            <div key={detail.label}>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{detail.label}</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{detail.value}</p>
            </div>
          ))}
        </div>
        {job.notes && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Notes</p>
            <p className="text-gray-700 dark:text-gray-200">{job.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CostBreakdown({ costBreakdown, totalCost }) {
  if (!costBreakdown) return null;

  // Check if this is the new CSI MasterFormat structure or legacy format
  const isCsiFormat = Object.keys(costBreakdown).some(key => key.match(/^\d{2} \d{2} \d{2}$/));

  let breakdown;

  if (isCsiFormat) {
    // New CSI MasterFormat structure: { "01 00 00": { title, subtotal, lineItems }, ... }
    breakdown = Object.entries(costBreakdown)
      .map(([code, data]) => ({
        key: code,
        label: data.title || `Division ${code.substring(0, 2)}`,
        amount: data.subtotal || 0,
        percentage: data.subtotal && totalCost
          ? ((data.subtotal / totalCost) * 100).toFixed(1)
          : 0,
        lineItems: data.lineItems || [],
      }))
      .filter((cat) => cat.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  } else {
    // Legacy format: { sitework: 123456, concrete: 234567, ... }
    const legacyCategories = [
      { key: 'sitework', label: 'Sitework' },
      { key: 'concrete', label: 'Concrete' },
      { key: 'masonry', label: 'Masonry' },
      { key: 'steel', label: 'Steel' },
      { key: 'carpentry', label: 'Carpentry' },
      { key: 'roofing', label: 'Roofing' },
      { key: 'interiorFinishes', label: 'Interior Finishes' },
      { key: 'mep', label: 'MEP' },
      { key: 'generalConditions', label: 'General Conditions' },
    ];

    breakdown = legacyCategories
      .map((cat) => ({
        ...cat,
        amount: costBreakdown[cat.key] || 0,
        percentage: costBreakdown[cat.key]
          ? ((costBreakdown[cat.key] / totalCost) * 100).toFixed(1)
          : 0,
      }))
      .filter((cat) => cat.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  }

  if (breakdown.length === 0) return null;

  // Find the max for scaling bars
  const maxAmount = Math.max(...breakdown.map((b) => b.amount));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cost Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {breakdown.map((item) => (
            <div key={item.key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {item.label}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {item.percentage}%
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100 w-24 text-right">
                    {formatCurrency(item.amount)}
                  </span>
                </div>
              </div>
              <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all"
                  style={{ width: `${(item.amount / maxAmount) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Total */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <span className="font-semibold text-gray-900 dark:text-gray-100">Total</span>
          <span className="font-bold text-lg text-gray-900 dark:text-gray-100">
            {formatCurrency(totalCost)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function CostMetrics({ job }) {
  const costPerSqFt = job.finalCost / job.squareFootage;

  const metrics = [
    {
      label: 'Cost per Square Foot',
      value: formatCurrency(costPerSqFt),
      subtext: 'Final project cost / total sq ft',
    },
    {
      label: 'Gross Margin',
      value: job.margin ? `${job.margin}%` : '—',
      subtext: job.margin ? (job.margin >= 12 ? 'Above target' : 'Below target') : 'Not recorded',
      status: job.margin ? (job.margin >= 12 ? 'success' : 'warning') : 'neutral',
    },
    {
      label: 'Total Project Value',
      value: formatCurrency(job.finalCost),
      subtext: `${formatSqFt(job.squareFootage)} building`,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Key Metrics</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
            >
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{metric.label}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{metric.value}</p>
              <p
                className={`text-xs mt-1 ${
                  metric.status === 'success'
                    ? 'text-green-600 dark:text-green-400'
                    : metric.status === 'warning'
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {metric.subtext}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function UseInQuoteButton({ job, onClick }) {
  return (
    <Card>
      <CardContent className="py-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Use as Reference</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Reference this job when creating a new quote for a similar project.
            </p>
          </div>
          <Button variant="primary" onClick={onClick}>
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Quote from This Job
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadJob() {
      setIsLoading(true);
      setError(null);
      try {
        const jobData = await jobsApi.getJobById(id);
        setJob(jobData);
      } catch (err) {
        setError(err.message || 'Failed to load job details');
      } finally {
        setIsLoading(false);
      }
    }
    loadJob();
  }, [id]);

  const handleBack = () => {
    navigate('/jobs');
  };

  const handleCreateQuote = () => {
    // Navigate to quote creation with this job as reference
    navigate(`/quotes/new?reference=${id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <BackButton onClick={handleBack} />
        <div className="p-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          <p className="font-medium">Error loading job</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="max-w-4xl mx-auto">
        <BackButton onClick={handleBack} />
        <div className="p-6 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg text-gray-700 dark:text-gray-200 text-center">
          <p>Job not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <BackButton onClick={handleBack} />
      <JobHeader job={job} />

      <div className="space-y-6">
        <CostMetrics job={job} />
        <JobOverview job={job} />
        <CostBreakdown costBreakdown={job.costBreakdown} totalCost={job.finalCost} />
        <UseInQuoteButton job={job} onClick={handleCreateQuote} />
      </div>
    </div>
  );
}

export default JobDetail;
